import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Notice,
  Progress,
  Radio,
  RadioGroup,
  SearchField,
  Stack,
  Switch,
} from '@eleveo/components-react';
import { CheckmarkIcon, CrossIcon } from '@eleveo/icons-react';
import { TFunction } from 'i18next';
import React, { useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { TeamSelectorListBox, TreeViewModel } from './TeamSelectorListBox';
import { useIntegrations } from '../../../../app/IntegrationsProvider';
import { TEST_IDS } from '../../../../constants/dataTestIds';
import { USER_SYNCHRONIZATION_FIELDS, USER_SYNC_FILTER_TEAMS_OPTIONS } from '../../../../scene/integrations/types';
import { Center } from '../../../Center';
import { ETL_INTEGRATION_TYPE, Values } from '../../../configuration/types';
import { useIntegrationTeams } from '../../teamselector/TeamSelectorProvider';
import { TeamOption } from '../../teamselector/types';

const TeamSelectorWrapper: React.FC<React.PropsWithChildren<{ t: TFunction }>> = ({ t, children }) => (
  <Flex column gap={20} className="flex-1 p-20 w-full h-full">
    <Heading as="h3" className="text-2xl text-blue-700 font-bold" testId={TEST_IDS.headerTeamSelector}>
      {t('myConfigurations.teamSelector.title')}
    </Heading>
    {children}
  </Flex>
);

export const TeamSelector: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const { isInReadOnlyMode, integrationType } = useIntegrations();

  const [searchValue, setSearchValue] = useState('');
  const [showSelectedTeamsOnly, setShowSelectedTeamsOnly] = useState(false);

  const {
    input: { value: teamSelectorMode, onChange: teamSelectorModeOnChange },
  } = useField(USER_SYNCHRONIZATION_FIELDS.teamSelectorMode, { subscription: { value: true } });

  const {
    input: { onChange: selectedTeamsOnChange },
  } = useField(USER_SYNCHRONIZATION_FIELDS.selectedTeams);

  const {
    teamOptions,
    isIntegrationTeamsError,
    isIntegrationTeamsLoading,
    isIntegrationDataFilled,
    isPermittedForIntegrationTeams,
    selectedTeams,
  } = useIntegrationTeams();

  const teamsToTreeView: TreeViewModel[] = useMemo(
    () => {
      if (!teamOptions) {
        return [];
      }

      const computeTreeForTeam = (team: TeamOption): TreeViewModel => {
        return {
          id: team.id,
          name: team.name,
          childItems: teamOptions.filter((teamOption) => teamOption.parentId === team.id).map(computeTreeForTeam),
        } as TreeViewModel;
      };

      return teamOptions.filter((g) => g.parentId == null).map(computeTreeForTeam);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teamOptions]
  );

  const fieldName = useMemo(() => {
    switch (integrationType) {
      case ETL_INTEGRATION_TYPE.wxcc:
      case ETL_INTEGRATION_TYPE.wxcalling:
        return 'organizationId';
      case ETL_INTEGRATION_TYPE.zoomcc:
        return 'accountId';
      case ETL_INTEGRATION_TYPE.msteams:
        return 'tenant';
      default:
        throw new Error('Invalid integration type!');
    }
  }, [integrationType]);

  const hasData = !!teamOptions?.length;
  const isLoading = isIntegrationTeamsLoading;

  if (isLoading) {
    return (
      <Center>
        <Progress />
      </Center>
    );
  }

  if (!isPermittedForIntegrationTeams) {
    return (
      <TeamSelectorWrapper t={t}>
        <Notice color="error" testId={TEST_IDS.noticeTeamSelectorMissingIntegrationPermission}>
          {t('myConfigurations.teamSelector.permissions.missing.forIntegrationTeams', {
            providerName: t(`providers.${integrationType}`),
          })}
        </Notice>
      </TeamSelectorWrapper>
    );
  }

  if (isIntegrationTeamsError) {
    return (
      <TeamSelectorWrapper t={t}>
        <Notice color="error" testId={TEST_IDS.noticeTeamSelectorIntegrationTeamsFailed}>
          {t('myConfigurations.teamSelector.error.integrationTeams', {
            providerName: t(`providers.${integrationType}`),
          })}
        </Notice>
      </TeamSelectorWrapper>
    );
  }

  if (!isIntegrationDataFilled) {
    return (
      <TeamSelectorWrapper t={t}>
        <Notice color="error" testId={TEST_IDS.noticeTeamSelectorIntegrationTeamsMissingIntegrationData}>
          {t('myConfigurations.teamSelector.info', {
            fieldName,
          })}
        </Notice>
      </TeamSelectorWrapper>
    );
  }

  if (isIntegrationDataFilled && !teamOptions?.length) {
    return (
      <TeamSelectorWrapper t={t}>
        <Notice color="info" testId={TEST_IDS.noticeTeamSelectorMissingIntegrationTeams}>
          {t('myConfigurations.teamSelector.teams.missingIntegrationTeams', {
            providerName: t(`providers.${integrationType}`),
          })}
        </Notice>
      </TeamSelectorWrapper>
    );
  }

  /**
   * Right now only supports teams without tree structure
   */
  const getFilteredTeams = (): TreeViewModel[] => {
    return teamsToTreeView
      .filter((team) => team.name.toLowerCase().includes(searchValue.toLowerCase()))
      .filter((team) => (showSelectedTeamsOnly ? selectedTeams.includes(team.id) : true));
  };

  const handleSelectAllTeams = () => {
    selectedTeamsOnChange(teamOptions?.map((to) => to.id));
  };

  const handleClearTeams = () => {
    selectedTeamsOnChange([]);
  };

  return (
    <>
      <TeamSelectorWrapper t={t}>
        {children}

        <Stack direction="vertical">
          <Stack>
            <RadioGroup
              aria-label={t('myConfigurations.teamSelector.importOptions')}
              defaultValue={teamSelectorMode || USER_SYNC_FILTER_TEAMS_OPTIONS.includeSelected}
              onChange={(v) => teamSelectorModeOnChange(v as Values<typeof USER_SYNC_FILTER_TEAMS_OPTIONS>)}
            >
              <Stack direction="horizontal" spacing={10}>
                <Radio value={USER_SYNC_FILTER_TEAMS_OPTIONS.includeSelected}>
                  {t('myConfigurations.teamSelector.filter.includeSelected')}
                </Radio>
                <Radio value={USER_SYNC_FILTER_TEAMS_OPTIONS.excludeSelected}>
                  {t('myConfigurations.teamSelector.filter.excludeSelected')}
                </Radio>
              </Stack>
            </RadioGroup>
          </Stack>

          <Stack>
            <SearchField
              className="w-full"
              onChange={(val) => setSearchValue(val)}
              testId={TEST_IDS.selectIntegrationTeamsSearchBar}
              value={searchValue}
              variant="outline"
            />
          </Stack>

          <Switch
            className="w-full"
            isSelected={showSelectedTeamsOnly}
            isDisabled={isInReadOnlyMode}
            onChange={(isSelected) => setShowSelectedTeamsOnly(isSelected)}
            testId={TEST_IDS.checkboxShowSelectedTeamsOnlyEnabled}
          >
            {t(`myConfigurations.teamSelector.showSelectedTeamsOnly`)}
          </Switch>
        </Stack>

        <Divider />

        {isIntegrationDataFilled && hasData && (
          <TeamSelectorListBox
            className="w-full"
            field={USER_SYNCHRONIZATION_FIELDS.selectedTeams}
            isDisabled={isInReadOnlyMode}
            selectedTeams={selectedTeams}
            teamsTree={getFilteredTeams()}
            testId={TEST_IDS.selectIntegrationTeams}
          />
        )}

        <Stack direction="vertical" spacing={10}>
          <Divider />
          <Box className="flex justify-between w-full">
            <Button
              isDisabled={teamsToTreeView.length === 0}
              isSmall
              onClick={handleSelectAllTeams}
              testId={TEST_IDS.selectAllTeamsButton}
              variant="quiet"
            >
              <CheckmarkIcon />
              {t('myConfigurations.teamSelector.selectAllTeamsButton')}
            </Button>
            <Button
              isDisabled={teamsToTreeView.length === 0}
              isSmall
              onClick={handleClearTeams}
              testId={TEST_IDS.clearTeamsButton}
              variant="quiet"
            >
              <CrossIcon className="text-orange-600" />
              {t('myConfigurations.teamSelector.clearTeamsButton')}
            </Button>
          </Box>
        </Stack>
      </TeamSelectorWrapper>
    </>
  );
};
